import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import type { Bilingual } from '../config/bilingual';
import { useAmbientTintStore } from './ambientTint';

export type ThemeMode = 'light' | 'dark';
export type ThemeColor = 'default' | 'pink' | 'blue' | 'yellow' | 'green' | 'orange' | 'black' | 'custom';
/** Global control shape preset: flat / min / mid / max.
 *  One switch drives corner rounding (--ui-radius-factor) AND spacing
 *  density (--ui-space-factor) — see the Shape section in style.css. */
export type Roundness = 'flat' | 'min' | 'mid' | 'max';

/**
 * How every card, menu and modal is filled (`data-card-style`):
 *  - `glass` — the tinted, blurred, shadowed glass panel (the default);
 *  - `normal` — no card background at all, content straight on the page.
 */
export type CardStyle = 'normal' | 'glass';

/** Where the glass tint comes from (`data-glass-tint`). */
export type GlassTint = 'lights' | 'accent' | 'neutral';

export const GLASS_TINTS: ReadonlyArray<{ id: GlassTint; label: Bilingual }> = [
  { id: 'lights', label: { en: 'Lights', sl: 'Luči' } },
  { id: 'accent', label: { en: 'Accent', sl: 'Poudarek' } },
  { id: 'neutral', label: { en: 'Neutral', sl: 'Nevtralno' } },
];

/** Tints for the two fixed modes (space-separated triplets, the CSS house style). */
const ACCENT_TINT = { hex: '#1fbe37', rgb: '31 190 55' };
const NEUTRAL_TINT = { hex: '#64748b', rgb: '100 116 139' };

export const LIGHT_COLORS: ThemeColor[] = ['default', 'pink', 'blue', 'yellow', 'green', 'orange', 'custom'];
export const DARK_COLORS: ThemeColor[] = ['default', 'pink', 'blue', 'yellow', 'green', 'orange', 'black', 'custom'];

/**
 * The palette's names, in both languages (`tt` at the picker picks the half).
 * A data table cannot know the reader's language, so it carries both.
 */
export const COLOR_INFO: Record<ThemeColor, { label: Bilingual; lightSwatch: string; darkSwatch: string }> = {
  default: {
    label: { en: 'Default', sl: 'Privzeto' },
    lightSwatch: 'bg-white border-neutral-300',
    darkSwatch: 'bg-slate-700 border-slate-500',
  },
  pink: {
    label: { en: 'Pink', sl: 'Roza' },
    lightSwatch: 'bg-pink-200 border-pink-400',
    darkSwatch: 'bg-pink-900 border-pink-700',
  },
  blue: {
    label: { en: 'Blue', sl: 'Modra' },
    lightSwatch: 'bg-sky-200 border-sky-400',
    darkSwatch: 'bg-sky-900 border-sky-700',
  },
  yellow: {
    label: { en: 'Yellow', sl: 'Rumena' },
    lightSwatch: 'bg-amber-200 border-amber-400',
    darkSwatch: 'bg-amber-900 border-amber-700',
  },
  green: {
    label: { en: 'Green', sl: 'Zelena' },
    lightSwatch: 'bg-emerald-200 border-emerald-400',
    darkSwatch: 'bg-emerald-900 border-emerald-700',
  },
  orange: {
    label: { en: 'Orange', sl: 'Oranžna' },
    lightSwatch: 'bg-orange-200 border-orange-400',
    darkSwatch: 'bg-orange-900 border-orange-700',
  },
  black: { label: { en: 'Black', sl: 'Črna' }, lightSwatch: '', darkSwatch: 'bg-neutral-950 border-neutral-700' },
  custom: {
    label: { en: 'Custom', sl: 'Po meri' },
    lightSwatch: 'bg-gradient-to-r from-pink-200 via-sky-200 to-amber-200 border-neutral-300',
    darkSwatch: 'bg-gradient-to-r from-pink-900 via-sky-900 to-amber-900 border-neutral-600',
  },
};

const GRAY_KEYS = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'] as const;
export { GRAY_KEYS };

/** Named color palettes (mirrors style.css .theme-* classes) */
export const COLOR_PALETTES: Record<string, Record<string, string>> = {
  default: {
    '50': '249 250 251',
    '100': '243 244 246',
    '200': '229 231 235',
    '300': '209 213 219',
    '400': '156 163 175',
    '500': '107 114 128',
    '600': '75 85 99',
    '700': '55 65 81',
    '800': '31 41 55',
    '900': '17 24 39',
    '950': '3 7 18',
  },
  pink: {
    '50': '255 241 246',
    '100': '254 228 238',
    '200': '252 207 225',
    '300': '240 180 204',
    '400': '200 140 166',
    '500': '160 110 132',
    '600': '120 80 100',
    '700': '90 58 74',
    '800': '62 38 50',
    '900': '42 22 34',
    '950': '26 10 18',
  },
  blue: {
    '50': '240 247 255',
    '100': '224 238 254',
    '200': '199 222 252',
    '300': '168 200 240',
    '400': '130 162 200',
    '500': '100 128 160',
    '600': '72 96 124',
    '700': '50 70 96',
    '800': '34 50 72',
    '900': '20 32 50',
    '950': '10 16 28',
  },
  yellow: {
    '50': '255 252 240',
    '100': '254 248 220',
    '200': '252 240 190',
    '300': '245 225 160',
    '400': '210 190 125',
    '500': '170 152 96',
    '600': '130 114 70',
    '700': '96 84 50',
    '800': '66 58 34',
    '900': '44 38 20',
    '950': '24 20 8',
  },
  green: {
    '50': '240 253 245',
    '100': '220 250 232',
    '200': '190 242 210',
    '300': '156 224 182',
    '400': '118 190 145',
    '500': '86 152 112',
    '600': '60 116 82',
    '700': '42 86 60',
    '800': '28 60 42',
    '900': '16 40 26',
    '950': '6 22 12',
  },
  orange: {
    '50': '255 247 237',
    '100': '254 236 214',
    '200': '252 218 186',
    '300': '248 195 152',
    '400': '220 165 118',
    '500': '180 132 90',
    '600': '140 98 64',
    '700': '104 72 44',
    '800': '72 48 28',
    '900': '48 30 16',
    '950': '28 14 4',
  },
  black: {
    '50': '250 250 250',
    '100': '240 240 240',
    '200': '217 217 217',
    '300': '184 184 184',
    '400': '138 138 138',
    '500': '94 94 94',
    '600': '51 51 51',
    '700': '31 31 31',
    '800': '20 20 20',
    '900': '10 10 10',
    '950': '3 3 3',
  },
};

// HSL → RGB conversion
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  s /= 100;
  l /= 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    return l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
  };
  return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
}

// Generate a tinted gray palette from a hue (0-360)
export function generatePalette(hue: number): Record<string, string> {
  const steps = [
    { key: '50', l: 97, s: 40 },
    { key: '100', l: 93, s: 35 },
    { key: '200', l: 86, s: 30 },
    { key: '300', l: 76, s: 28 },
    { key: '400', l: 60, s: 20 },
    { key: '500', l: 46, s: 16 },
    { key: '600', l: 35, s: 14 },
    { key: '700', l: 25, s: 16 },
    { key: '800', l: 15, s: 20 },
    { key: '900', l: 9, s: 30 },
    { key: '950', l: 4, s: 50 },
  ];
  const result: Record<string, string> = {};
  for (const { key, l, s } of steps) {
    const [r, g, b] = hslToRgb(hue, s, l);
    result[key] = `${r} ${g} ${b}`;
  }
  return result;
}

export const useThemeStore = defineStore('theme', () => {
  const mode = ref<ThemeMode>('dark');
  const color = ref<ThemeColor>('default');
  const customHue = ref(200);
  const roundness = ref<Roundness>('mid');
  const cardStyle = ref<CardStyle>('glass');
  const glassTint = ref<GlassTint>('lights');
  const isDark = computed(() => mode.value === 'dark');

  const ambientTint = useAmbientTintStore();

  /**
   * The tint the glass surfaces are painted with. `lights` follows the lit RGB
   * lamps — the room in view on a room page, the whole building elsewhere —
   * and holds its last colour while they are off, so the UI does not flash
   * grey every time the room goes dark.
   */
  const tint = computed<{ hex: string; rgb: string }>(() => {
    if (glassTint.value === 'accent') return ACCENT_TINT;
    if (glassTint.value === 'neutral') return NEUTRAL_TINT;
    return ambientTint.effective ?? ambientTint.lastMix ?? NEUTRAL_TINT;
  });

  const applyTheme = () => {
    const el = document.documentElement;
    // Mode class
    el.classList.toggle('dark', mode.value === 'dark');
    // Shape preset (flat / min / mid / max) — drives radius + spacing factors
    el.dataset.roundness = roundness.value;
    // Card fill (normal | glass) and tint source
    el.dataset.cardStyle = cardStyle.value;
    el.dataset.glassTint = glassTint.value;
    el.style.setProperty('--ui-tint', tint.value.rgb);
    el.style.setProperty('--ui-tint-hex', tint.value.hex);
    // Remove all theme-* classes
    el.className = el.className
      .replace(/\btheme-\w+/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    // Clear any custom inline vars
    for (const key of GRAY_KEYS) {
      el.style.removeProperty(`--gray-${key}`);
    }

    if (color.value === 'custom') {
      el.classList.add('theme-custom');
      const palette = generatePalette(customHue.value);
      for (const key of GRAY_KEYS) {
        el.style.setProperty(`--gray-${key}`, palette[key]);
      }
    } else if (color.value !== 'default') {
      el.classList.add(`theme-${color.value}`);
    }
  };

  // A lit lamp changing colour re-tints every glass surface. Cheap: the mix is
  // one pass over the devices store, and the write is one CSS variable.
  watch(tint, () => {
    const el = document.documentElement;
    el.style.setProperty('--ui-tint', tint.value.rgb);
    el.style.setProperty('--ui-tint-hex', tint.value.hex);
  });

  const save = () => {
    localStorage.setItem('themeMode', mode.value);
    localStorage.setItem('themeColor', color.value);
    localStorage.setItem('themeCustomHue', String(customHue.value));
    localStorage.setItem('uiRoundness', roundness.value);
    localStorage.setItem('uiCardStyle', cardStyle.value);
    localStorage.setItem('uiGlassTint', glassTint.value);
  };

  const initTheme = () => {
    const savedMode = localStorage.getItem('themeMode') as ThemeMode | null;
    const savedColor = localStorage.getItem('themeColor') as ThemeColor | null;
    const savedHue = localStorage.getItem('themeCustomHue');

    if (savedMode === 'light' || savedMode === 'dark') {
      mode.value = savedMode;
    } else {
      // Legacy migration
      const legacy = localStorage.getItem('colorStyle') || localStorage.getItem('theme');
      if (legacy === 'light') {
        mode.value = 'light';
      } else if (legacy === 'dark' || legacy === 'dark-black') {
        mode.value = 'dark';
      } else if (legacy === 'pink' || legacy === 'blue') {
        mode.value = 'light';
      } else {
        mode.value = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }

      // Migrate color from legacy
      if (legacy === 'dark-black') color.value = 'black';
      else if (legacy === 'pink') color.value = 'pink';
      else if (legacy === 'blue') color.value = 'blue';
    }

    if (savedColor && [...LIGHT_COLORS, ...DARK_COLORS].includes(savedColor)) {
      color.value = savedColor;
    }
    if (savedHue) customHue.value = Number(savedHue) || 200;

    // Validate color is available for current mode
    const available = mode.value === 'light' ? LIGHT_COLORS : DARK_COLORS;
    if (!available.includes(color.value)) color.value = 'default';

    // Shape preset (flat / min / mid / max)
    const savedRoundness = localStorage.getItem('uiRoundness') as Roundness | null;
    if (savedRoundness === 'flat' || savedRoundness === 'min' || savedRoundness === 'mid' || savedRoundness === 'max') {
      roundness.value = savedRoundness;
    }

    // Card fill / tint source
    const savedCardStyle = localStorage.getItem('uiCardStyle');
    if (savedCardStyle === 'normal' || savedCardStyle === 'glass') cardStyle.value = savedCardStyle;

    const savedTint = localStorage.getItem('uiGlassTint');
    if (savedTint && GLASS_TINTS.some((entry) => entry.id === savedTint)) {
      glassTint.value = savedTint as GlassTint;
    }

    applyTheme();
  };

  const setMode = (m: ThemeMode) => {
    mode.value = m;
    const available = m === 'light' ? LIGHT_COLORS : DARK_COLORS;
    if (!available.includes(color.value)) color.value = 'default';
    save();
    applyTheme();
  };

  const setColor = (c: ThemeColor) => {
    color.value = c;
    save();
    applyTheme();
  };

  const setCustomHue = (hue: number) => {
    customHue.value = hue;
    save();
    if (color.value === 'custom') applyTheme();
  };

  const setRoundness = (r: Roundness) => {
    roundness.value = r;
    save();
    applyTheme();
  };

  const setCardStyle = (style: CardStyle) => {
    cardStyle.value = style;
    save();
    applyTheme();
  };

  const setGlassTint = (tintMode: GlassTint) => {
    glassTint.value = tintMode;
    save();
    applyTheme();
  };

  // Legacy compat
  const toggleTheme = () => setMode(isDark.value ? 'light' : 'dark');
  const cycleTheme = () => setMode(isDark.value ? 'light' : 'dark');
  const setTheme = (t: 'dark' | 'light') => setMode(t);

  return {
    mode,
    color,
    customHue,
    roundness,
    cardStyle,
    glassTint,
    tint,
    isDark,
    initTheme,
    setMode,
    setColor,
    setCustomHue,
    setRoundness,
    setCardStyle,
    setGlassTint,
    toggleTheme,
    cycleTheme,
    setTheme,
  };
});
